import {
    Authentication,
    AuthenticationManager,
    AuthorizationDecision,
    BadCredentialsError,
    DefaultAuthorizationManager,
    ServerAuthenticationEntryPoint,
    ServerAuthenticationSuccessHandler
} from './types.js';
import headers from './http-headers.js';
import httpBasic from './http-basic.js';
import resourceServer from './oauth2-resource-server.js';
import serverBearerTokenAuthenticationConverter from './oauth2/token-converter.js';
import bearerTokenServerAuthenticationEntryPoint from './oauth2/token-entry-point.js';
import {BadJwtError, Jwt, JwtError} from './oauth2/jwt-auth-manager.js';
import {jwtVerifier, JwtVerifyError} from '@interopio/gateway/jose/jwt';
import {anyExchange, ServerWebExchangeMatcher, ServerWebExchangeMatcherEntry, match, NO_MATCH} from '../util/matchers.js';
import {errorFilter} from './error-filter.js';
import {delegatingEntryPoint} from './delegating-entry-point.js';
import delegatingAuthorizationManager from './delegating-authorization-manager.js';
import authorizationFilter from './authorization-filter.js';
import {exchangeFilter} from './exchange-filter.js';
import {SecurityContext} from './security-context.js';
import cors, { type CorsConfigSource} from '../cors.js';
import type {Middleware} from '../types.js';
import {type AsyncLocalStorage} from 'node:async_hooks';
import type { ServerWebExchange} from '../../../types/web/server';

export type HttpSecurityConfig = {
    authorize?: Array<[
            'any-exchange' | ServerWebExchangeMatcher,
        ( { access: 'permitted' | 'denied' | 'authenticated' }
            | { access: 'has-any-authority', authorities: string[] }
            | { access: 'has-ip-address', address: string })
    ]>,
    headers?: {
        disabled?: boolean;
        xss?: { disabled?: boolean };
    },
    cors?: {
        disabled?: boolean;
    }
    basic?: {
        disabled?: boolean;
        realm?: string;
        user?: {
            name: string,
            password?: string // generated password, if not provided
            authorities?: string[]
        }
    },
    jwt?: {
        disabled?: boolean;
        issuerUri?: string; // base URI for the issuer, e.g. 'https://example.com'
        issuer?: string;
        audience?: string | string[]; // audience for the JWT, e.g. 'https://example.com/api'
    }
}

const filterOrder = {
    first: Number.MAX_SAFE_INTEGER,
    http_headers: 1 * 100,
    https_redirect: 2 * 100,
    cors: 3 * 100,
    http_basic: 6 * 100,
    authentication: 8 * 100,
    security_context_server_web_exchange: 15 * 100,
    error_translation: 18 * 100,
    authorization: 19 * 100,
    last: Number.MAX_SAFE_INTEGER
}
const filterOrderSymbol = Symbol.for("filterOrder");

export default (config: HttpSecurityConfig,
                context: {
                    storage: AsyncLocalStorage<{ securityContext?: Promise<SecurityContext> }>
                    corsConfigSource?: CorsConfigSource,
                }): Middleware => {
    const middleware: Middleware = [];

    class ServerHttpSecurity {
        #authenticationEntryPoint?: ServerAuthenticationEntryPoint;
        readonly #defaultEntryPoints: Array<[ServerWebExchangeMatcher, ServerAuthenticationEntryPoint]> = [];
        manager?: AuthenticationManager;

        get authenticationEntryPoint(): ServerAuthenticationEntryPoint | undefined {
            if (this.#authenticationEntryPoint !== undefined || this.#defaultEntryPoints.length === 0) {
                return this.#authenticationEntryPoint!;
            }
            if (this.#defaultEntryPoints.length === 1) {
                return this.#defaultEntryPoints[0][1];
            }
            return delegatingEntryPoint({
                entryPoints: this.#defaultEntryPoints,
                defaultEntryPoint: this.#defaultEntryPoints[this.#defaultEntryPoints.length - 1][1]
            });
        }


        build() {
            if (config.headers !== undefined && config.headers.disabled !== true) {
                const writer = headers(config.headers);
                writer[filterOrderSymbol] = filterOrder.http_headers;
                middleware.push(writer);
            }
            if (config.cors?.disabled !== true && context.corsConfigSource !== undefined) {
                const filter: (ctx: ServerWebExchange, next: () => Promise<void>) => Promise<void> = cors({corsConfigSource: context.corsConfigSource });
                filter[filterOrderSymbol] = filterOrder.cors;
                middleware.push(filter);
            }

            if (config.basic !== undefined && config.basic?.disabled !== true) {
                const username = config.basic.user?.name.toLowerCase();
                const password = config.basic.user?.password ?? '';
                const authorities = config.basic.user?.authorities ?? [];
                const manager: AuthenticationManager = async (auth) => {
                    const principal = auth['principal'];
                    const credentials = auth['credentials'];
                    if (principal.toLowerCase() !== username || credentials !== password) {
                        throw new BadCredentialsError('Invalid username or password');
                    }
                    return {type: 'UsernamePassword', authenticated: true, principal, credentials, authorities: [...authorities]} as Authentication;
                }

                const defaultSuccessHandlers: ServerAuthenticationSuccessHandler[] = [
                    async ({exchange: _, next}, _authentication) => {
                        return next();
                    }
                ];


                const filter = httpBasic({
                    storage: context.storage,
                    manager,
                    defaultEntryPoints: this.#defaultEntryPoints,
                    defaultSuccessHandlers,
                });
                filter[filterOrderSymbol] = filterOrder.http_basic;
                middleware.push(filter);
            }
            if (config.jwt !== undefined && config.jwt.disabled !== true) {

                const verifier = jwtVerifier({
                    issuerBaseUri: config.jwt.issuerUri,
                    issuer: config.jwt.issuer,
                    audience: config.jwt.audience
                });
                const decoder = async (token: string): Promise<Jwt> => {
                    try {
                        const {payload} = await verifier(token);
                        return {
                            subject: payload.sub,
                            getClaimAsString(claim: string): string | undefined {
                                return payload[claim] as string | undefined;
                            }
                        };
                    } catch (e) {
                        if (e instanceof JwtVerifyError) {
                            throw new BadJwtError(e.message, {cause: e});
                        }
                        throw new JwtError("error occurred while attempting to decoding jwt", {cause: e});
                    }
                }


                const authenticationConverter = serverBearerTokenAuthenticationConverter({uriQueryParameter: true});
                const authenticationConverterMatcher: ServerWebExchangeMatcher = async (exchange) => {
                    try {
                        const a = await authenticationConverter(exchange)
                        if (a === undefined) {
                            return NO_MATCH;
                        }
                        return match();
                    }
                    catch (e) {
                        return NO_MATCH;
                    }
                }

                const entryPoint = bearerTokenServerAuthenticationEntryPoint({});

                this.#defaultEntryPoints.push([authenticationConverterMatcher, entryPoint]);

                const filter = resourceServer({
                    storage: context.storage,
                    entryPoint: entryPoint,
                    converter: authenticationConverter,
                    jwt: {decoder}});
                filter[filterOrderSymbol] = filterOrder.authentication;
                middleware.push(filter);

            }
            const exchangeF = exchangeFilter({storage: context.storage});
            middleware.push(exchangeF);
            exchangeF[filterOrderSymbol] = filterOrder.security_context_server_web_exchange;


            if (config.authorize !== undefined) {
                const errorFf = errorFilter({authenticationEntryPoint: this.authenticationEntryPoint});
                errorFf[filterOrderSymbol] = filterOrder.error_translation;
                middleware.push(errorFf)
                const buildAuthorizationManager = (authorize: HttpSecurityConfig['authorize']) => {
                    const mappings: ServerWebExchangeMatcherEntry<DefaultAuthorizationManager<{
                        exchange: ServerWebExchange
                    }>>[] = [];
                    let anyExchangeRegistered = false;
                    for (const [matcher, access] of authorize ?? []) {
                        let serverMatcher: ServerWebExchangeMatcher;
                        if (matcher === 'any-exchange') {
                            anyExchangeRegistered = true;
                            serverMatcher = anyExchange;
                        } else if (anyExchangeRegistered) {
                            throw new Error("Cannot register other matchers after 'any-exchange' matcher");
                        } else {
                            serverMatcher = matcher;
                        }
                        let manager: DefaultAuthorizationManager<{ exchange: ServerWebExchange }>;
                        if (access.access === 'permitted') {
                            manager = new DefaultAuthorizationManager(async () => new AuthorizationDecision(true));
                            manager.toString = () => 'AuthorizationManager[permitted]';
                        } else if (access.access === 'denied') {
                            manager = new DefaultAuthorizationManager(async () => new AuthorizationDecision(false));
                            manager.toString = () => 'AuthorizationManager[denied]';
                        } else if (access.access === 'authenticated') {
                            manager = new DefaultAuthorizationManager(async (p: Promise<Authentication | undefined>) => {
                                const authentication = await p;
                                if (authentication !== undefined) {
                                    return new AuthorizationDecision(authentication.authenticated);
                                }
                                return new AuthorizationDecision(false);
                            });
                            manager.toString = () => 'AuthorizationManager[authenticated]';
                        } else {
                            throw new Error(`Unknown access type: ${JSON.stringify(access)}`);
                        }
                        mappings.push([serverMatcher, manager]);
                    }
                    return delegatingAuthorizationManager({mappings});
                }
                const manager = buildAuthorizationManager(config.authorize);
                const filter = authorizationFilter({manager, storage: context.storage});
                filter[filterOrderSymbol] = filterOrder.authorization;
                middleware.push(filter);

            }

            middleware.sort((a, b) => {
                const aOrder = a[filterOrderSymbol] ?? filterOrder.last;
                const bOrder = b[filterOrderSymbol] ?? filterOrder.last;
                return aOrder - bOrder;
            });
        }
    }


    const security = new ServerHttpSecurity();
    security.build();


    return middleware;
};
